import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { History } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import QueryBar from "../components/work/QueryBar";
import { TypeIcon, PriorityIcon, Avatar, StatusPill } from "../components/work/workMeta";
import { fmtH, fmtMoney } from "../components/work/WorkLog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const RECENT_KEY = "apha_recent_queries";
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };

export default function WorkQuery() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState(readRecent);
  const canSeePrice = ["PM", "LEAD", "ADMIN", "EXECUTIVE", "AUDITOR"].includes(user?.role);

  const run = useCallback((q) => {
    setParams(q ? { q } : {}, { replace: true });
    if (!q) { setResult(null); setError(null); return; }
    setBusy(true);
    api.get("/work/query", { params: { q } })
      .then((r) => {
        setResult(r.data); setError(null);
        const next = [q, ...readRecent().filter((x) => x !== q)].slice(0, 6);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next)); setRecent(next);
      })
      .catch((e) => { setError(apiError(e)); setResult({ items: [], count: 0 }); })
      .finally(() => setBusy(false));
  }, [setParams]);
  useEffect(() => { if (query) run(query); }, [query, run]);

  const items = result?.items || [];
  return (
    <div className="space-y-5" data-testid="work-query-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("workQuery")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("workQueryIntro")}</p>
      </div>
      <QueryBar value={query} onRun={run} error={error} busy={busy} count={result?.count} />
      {recent.length > 0 && !query && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="recent-queries">
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground mr-1"><History className="h-3 w-3" />{t("recentQueries")}</span>
          {recent.map((q) => <button key={q} type="button" data-testid="recent-query" onClick={() => run(q)} className="font-mono text-[11px] rounded-full border bg-white px-2.5 py-1 hover:border-[#1E3A8A]/50 max-w-[24rem] truncate">{q}</button>)}
        </div>
      )}
      {result && (
        <div className="apha-card overflow-x-auto" data-testid="query-results">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t("projectLabel")}</TableHead><TableHead></TableHead><TableHead>{t("key")}</TableHead><TableHead>{t("title")}</TableHead>
              <TableHead>{t("status")}</TableHead><TableHead>{t("assignee")}</TableHead><TableHead>{t("priority")}</TableHead>
              <TableHead>{t("dueDate")}</TableHead><TableHead className="text-right capitalize">{t("loggedShort")} / {t("estimateShort")}</TableHead>
              {canSeePrice && <TableHead className="text-right">{t("price")}</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} className="cursor-pointer" data-testid={`query-row-${i.key}`} onClick={() => navigate(`/work/${i.key}`)}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{i.project_key}</TableCell>
                  <TableCell><TypeIcon type={i.type} /></TableCell>
                  <TableCell className="text-xs font-medium whitespace-nowrap">{i.key}</TableCell>
                  <TableCell className="max-w-[26rem] truncate">{i.title}{i.is_blocked && <span className="ml-2 text-[10px] text-[#991B1B] font-semibold">BLOCKED</span>}</TableCell>
                  <TableCell><StatusPill name={i.status_name} category={i.status_category} /></TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-xs"><Avatar name={i.assignee_name} />{i.assignee_name || t("unassigned")}</span></TableCell>
                  <TableCell><span className="inline-flex items-center gap-1 text-xs"><PriorityIcon priority={i.priority} />{t(i.priority)}</span></TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{i.due_date || "—"}</TableCell>
                  <TableCell className="text-xs text-right whitespace-nowrap">{fmtH(i.logged_hours || 0)} / {fmtH(i.estimate_hours)}</TableCell>
                  {canSeePrice && <TableCell className="text-xs text-right whitespace-nowrap">{i.price != null ? fmtMoney(i.price, i.currency) : "—"}</TableCell>}
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={canSeePrice ? 10 : 9} className="text-sm text-muted-foreground">{t("noWorkItems")}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
